import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

/**
 * Genes whose body overlaps `pos` (1-based) on `normalizedChrom` (chr-prefixed),
 * from the GENCODE-backed annotation endpoint. Each gene is
 * `{ gene_symbol, start, end, chrom }` with 0-based half-open coordinates.
 * Returns `[]` on any error so the caller falls back to a fixed window.
 */
export const fetchOverlappingGenes = async (normalizedChrom, pos) => {
  try {
    // 1 bp probe window; the endpoint overlap test (gene.start <= end &&
    // gene.end >= start) returns every gene whose body contains `pos`.
    const region = `${normalizedChrom}:${pos}-${pos + 1}`
    const { data } = await axios.get(`${API_BASE_URL}/unified/annotations/genes`, {
      params: { region, detail: 'names' },
    })
    return Array.isArray(data?.genes) ? data.genes : []
  } catch (error) {
    console.warn('Gene span lookup failed; using fixed IGV window', error)
    return []
  }
}

// Padding (bp) added on each side of the gene-union span.
export const GENE_PADDING = 1000
// Half-window (bp) for an intergenic variant with no overlapping gene → 20kb total.
export const INTERGENIC_HALF_WINDOW = 10000

// ---- Gene-anchored IGV viewer (gene browser tab + CNV/SV table) -----------
// Hard cap (bp) on the span of any BAM region we ask the backend to slice. The
// largest human gene (DMD ~2.2Mb) + padding stays well under this; the cap only
// bites on a pathological gene or an accidental full-CNV window. The backend
// enforces its own 5Mb ceiling — this keeps us comfortably below it.
export const CNV_BAM_CAP = 3000000
// Intergenic CNV/SV: show the whole segment (+ padding) when it's at most this
// wide, otherwise fall back to a fixed window around the start breakpoint.
export const INTERGENIC_CAP = 200000
// Half-window (bp) around an intergenic CNV/SV breakpoint → 50kb total.
export const CNV_INTERGENIC_HALF_WINDOW = 25000

/**
 * Bounds `{ start, end }` (1-based, inclusive) for the IGV view and the BAM
 * region: the union of the overlapping genes' spans + GENE_PADDING, always
 * including the variant `pos`. For an intergenic variant (no genes / lookup
 * failed) a ±10kb window is used instead of hugging the variant too tightly.
 *
 * Gene coords from GENCODE are 0-based half-open; these bounds are 1-based.
 */
export const computeBounds = (genes, pos) => {
  if (!genes || genes.length === 0) {
    return { start: Math.max(1, pos - INTERGENIC_HALF_WINDOW), end: pos + INTERGENIC_HALF_WINDOW }
  }
  const spanStart0 = Math.min(...genes.map((g) => g.start)) // 0-based
  const spanEnd0 = Math.max(...genes.map((g) => g.end)) // 0-based, exclusive
  let start = Math.max(1, spanStart0 + 1 - GENE_PADDING) // 0-based -> 1-based, clamp
  let end = spanEnd0 + GENE_PADDING
  // Guarantee the variant is on screen even if it sits outside the returned span.
  start = Math.min(start, Math.max(1, pos - GENE_PADDING))
  end = Math.max(end, pos + GENE_PADDING)
  return { start, end }
}

/** IGV locus string for the computed bounds (1-based). */
export const computeLocus = (genes, pos, normalizedChrom) => {
  const { start, end } = computeBounds(genes, pos)
  return `${normalizedChrom}:${start}-${end}`
}

/**
 * Bounds `{ start, end }` (1-based, inclusive) for an explicit gene span, with
 * no variant anchor. `gene` is `{ start, end }` in 0-based half-open GENCODE
 * coords (as returned by the gene-search endpoint). Used by the gene-anchored
 * IGV viewer.
 */
export const computeBoundsForGene = ({ start, end }, padding = GENE_PADDING) => {
  const s = Math.max(1, start + 1 - padding) // 0-based -> 1-based, clamp
  const e = end + padding
  return { start: s, end: e }
}

/** Shrink a window to at most `cap` bp, keeping the start breakpoint visible. */
const clampSpan = ({ start, end }, cap) => (end - start > cap ? { start, end: start + cap } : { start, end })

/**
 * Bounds `{ start, end }` (1-based, inclusive) for a CNV/SV, anchored to one of
 * its overlapping genes so the BAM slice stays bounded even for a multi-Mb CNV.
 *
 *   - `gene` given → the gene span (+ padding), clamped to `cap`.
 *   - intergenic (no gene) → the whole segment (+ padding) when it's ≤
 *     INTERGENIC_CAP, otherwise ±CNV_INTERGENIC_HALF_WINDOW around `segStart`.
 *
 * The CNV/SV segment itself is drawn as a feature by the caller and may extend
 * far beyond these bounds — this only controls the view + BAM window.
 */
export const computeBoundsForCnv = ({ segStart, segEnd, gene, cap = CNV_BAM_CAP }) => {
  if (gene && gene.start != null && gene.end != null) {
    return clampSpan(computeBoundsForGene(gene), cap)
  }
  const span = segEnd != null && segStart != null ? segEnd - segStart : 0
  if (span > 0 && span <= INTERGENIC_CAP) {
    return { start: Math.max(1, segStart - GENE_PADDING), end: segEnd + GENE_PADDING }
  }
  return {
    start: Math.max(1, segStart - CNV_INTERGENIC_HALF_WINDOW),
    end: segStart + CNV_INTERGENIC_HALF_WINDOW,
  }
}
